import {
  MAX_VIDEO_DURATION_SEC,
  VIDEO_ALLOWED_TYPES,
  VIDEO_MAX_UPLOAD_BYTES,
} from '../../../common/constants/videoToGifLimits'

export type ConverterVideoLoadErrorKey = 'invalidType' | 'tooLarge' | 'tooLong' | 'failed'

export class ConverterVideoLoadError extends Error {
  readonly key: ConverterVideoLoadErrorKey
  constructor(key: ConverterVideoLoadErrorKey) {
    super(key)
    this.key = key
  }
}

export interface LoadedVideo {
  file: File
  objectUrl: string
  duration: number
  width: number
  height: number
}

/** Validates + reads video metadata (duration/dimensions) via an off-DOM <video>
 * element BEFORE any network call — the pre-flight gate for the trim/settings UI.
 * The duration cap can only be checked once metadata loads (unlike file size/type,
 * which are known upfront). */
export const loadConverterVideoFile = (file: File): Promise<LoadedVideo> => {
  if (!VIDEO_ALLOWED_TYPES.has(file.type)) return Promise.reject(new ConverterVideoLoadError('invalidType'))
  if (file.size > VIDEO_MAX_UPLOAD_BYTES) return Promise.reject(new ConverterVideoLoadError('tooLarge'))

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      if (video.duration > MAX_VIDEO_DURATION_SEC) {
        URL.revokeObjectURL(objectUrl)
        reject(new ConverterVideoLoadError('tooLong'))
        return
      }
      resolve({file, objectUrl, duration: video.duration, width: video.videoWidth, height: video.videoHeight})
    }
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new ConverterVideoLoadError('failed'))
    }
    video.src = objectUrl
  })
}
